<template>
  <ModalComponent
    size="lg"
    submitButtonText=""
    :hideSubmitButton="true"
    @setForceToggle="setForceToggle"
    @close="close"
  >
    <template #title>
      {{ t("dashboard.portfolio.confirmSellTitle") }}
    </template>

    <template #body>
      <div class="p-3">
        <p>{{ confirmationMessage }}</p>

        <!-- Two-Phase Summary for Minimum Price Strategy -->
        <div
          v-if="selectedStrategy.type === 'minimum'"
          class="border rounded p-3 mb-3 bg-light"
        >
          <div class="d-flex align-items-start mb-3">
            <span
              class="badge bg-primary-subtle text-primary fw-semibold me-2 mt-1"
            >
              1
            </span>
            <div>
              <div class="fw-semibold">
                {{
                  t("dashboard.portfolio.confirm_phase_1_title", {
                    duration: selectedStrategy.durationMonths,
                  })
                }}
              </div>
              <div class="small text-muted">
                {{
                  t("dashboard.portfolio.confirm_phase_1_description", {
                    price: selectedStrategy.minimumPrice?.toFixed(2),
                  })
                }}
              </div>
            </div>
          </div>
          <div class="d-flex align-items-start">
            <span
              class="badge bg-success-subtle text-success fw-semibold me-2 mt-1"
            >
              2
            </span>
            <div>
              <div class="fw-semibold">
                {{
                  t("dashboard.portfolio.confirm_phase_2_title", {
                    duration: selectedStrategy.durationMonths,
                  })
                }}
              </div>
              <div class="small text-muted">
                {{ t("dashboard.portfolio.confirm_phase_2_description") }}
              </div>
            </div>
          </div>
        </div>

        <DurationWarnings
          v-if="
            selectedStrategy.type === 'minimum' &&
            selectedStrategy.durationMonths
          "
          :durationMonths="selectedStrategy.durationMonths"
        />

        <div class="mt-3 border-top pt-3">
          <h6 class="mb-2">
            {{ t("dashboard.portfolio.selected_certificates_summary") }}
          </h6>
          <div class="row g-2">
            <div class="col-6">
              <small class="text-muted d-block">{{
                t("dashboard.portfolio.totalMwhToSell")
              }}</small>
              <div class="fw-semibold">{{ totalMwhToSell }} MWh</div>
            </div>
            <div class="col-6">
              <small class="text-muted d-block">{{
                t("dashboard.portfolio.technologies")
              }}</small>
              <div>{{ selectedTechnologies }}</div>
            </div>
            <div class="col-6">
              <small class="text-muted d-block">{{
                t("dashboard.portfolio.production_months")
              }}</small>
              <div>{{ selectedProductionMonths }}</div>
            </div>
            <div class="col-6">
              <small class="text-muted d-block">{{
                t("dashboard.portfolio.footer.production_support")
              }}</small>
              <div>{{ summarySupport }}</div>
            </div>
          </div>
          <h6 class="mt-3 mb-2">
            {{ t("dashboard.portfolio.spotSalesPortfolio") }}
          </h6>
          <div class="d-flex align-items-center gap-2">
            <i class="fas fa-arrow-right text-success"></i>
            <span class="fw-bold">{{ strategyDisplayText }}</span>
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <button type="button" class="btn btn-link text-secondary" @click="close">
        {{ t("common.back") }}
      </button>
      <button
        type="button"
        class="btn btn-success"
        :disabled="isLoading"
        @click="confirm"
      >
        <span
          v-if="isLoading"
          class="spinner-border spinner-border-sm me-2"
          role="status"
          aria-hidden="true"
        ></span>
        {{ t("common.actions.confirm_sell") }}
      </button>
    </template>
  </ModalComponent>
</template>

<script setup lang="ts">
import ModalComponent from "@/components/common/ModalComponent.vue";
import DurationWarnings from "@/dashboard/common/DurationWarnings.vue";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{
  totalMwhToSell: number;
  selectedTechnologies: string;
  selectedProductionMonths: string;
  summarySupport: string;
  selectedStrategy: {
    type: "market" | "minimum";
    minimumPrice?: number;
    durationMonths: number;
    isValid: boolean;
  };
  isLoading: boolean;
}>();

const emit = defineEmits<{
  confirm: [];
}>();

const { t } = useI18n();

const confirmationMessage = computed(() => {
  const mwh = props.totalMwhToSell;

  const messages = {
    market: t("dashboard.portfolio.confirmSellMessage", {
      mwh,
    }),
    minimum: t("dashboard.portfolio.confirm_sell_minimum_price_message", {
      mwh,
    }),
  };

  return messages[props.selectedStrategy.type];
});

const strategyDisplayText = computed(() => {
  if (props.selectedStrategy.type === "market") {
    return t("dashboard.portfolio.footer.strategy_auto_high");
  }

  const price = props.selectedStrategy.minimumPrice;
  const duration = props.selectedStrategy.durationMonths;
  if (price !== undefined && typeof price === "number" && !isNaN(price)) {
    return t("dashboard.portfolio.footer.strategy_min_price_detailed", {
      price: price.toFixed(2),
      duration,
    });
  }

  return t("dashboard.portfolio.footer.strategy_min_price");
});

const forceToggle = ref<((value: boolean) => void) | null>(null);

const setForceToggle = (fn: (value: boolean) => void) => {
  forceToggle.value = fn;
};

const open = () => {
  forceToggle.value?.(true);
};

const close = () => {
  forceToggle.value?.(false);
};

const confirm = () => {
  close();
  emit("confirm");
};

defineExpose({ open, close });
</script>
